import {
    ApplicationConfig,
    APP_INITIALIZER,
    ErrorHandler,
    inject,
    isDevMode,
    provideAppInitializer,
    provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter, Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { getClient } from '@sentry/angular';
import { HttpBackend, HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { providePrimeNG } from 'primeng/config';
import { TranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { provideServiceWorker } from '@angular/service-worker';
import { MessageService, ConfirmationService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { provideEchartsCore } from 'ngx-echarts';

import { routes } from './app.routes';
import DkPreset from '../styles/utils/preset';

import { AppConfigService } from './core/services/app-config.service';
import { LanguageInitializerService } from './core/services/language-initializer.service';
import { AppInitializerService } from './core/services/app-initializer.service';
import { AnalyticsService } from './core/services/analytics.service';
import { AuthService } from './core/services/auth.service';
import { XAppInterceptor } from './core/interceptors/x-app.interceptor';
import { AuthErrorInterceptor } from './core/interceptors/auth-error.interceptor';
import { ApiToastInterceptor } from './core/interceptors/api-toast.interceptor';
import { NoCacheInterceptor } from './core/interceptors/no-cache.interceptor';
import { BlobCacheBusterInterceptor } from './core/interceptors/blob-cache-buster.interceptor';
import { NavigationService } from './core/services/navigation.service';
import { PLAIN_HTTP_CLIENT } from './core/tokens/http-tokens';
import { ConfigAwareTranslateLoader } from './core/services/config-aware-translate-loader.service';
import {
    AccessEnterpriseProxy,
    EnterpriseProxy,
    AccessRoleProxy,
    API_BASE_URL,
    CropMapUserProxy,
    CropMapPlantationProxy,
    CropSeasonProxy,
    FieldObservationProxy,
    FieldObservationQuestionnaireProxy,
    FieldObservationUserProxy,
    LookupProxy,
    UserProxy,
} from './proxy/proxies';
import { environment } from '../environments/environment';
import { initSentryFromDsn } from './core/sentry/sentry-init';

export const appConfig: ApplicationConfig = {
    providers: [
        provideBrowserGlobalErrorListeners(),
        provideRouter(routes),
        provideHttpClient(
            withInterceptors([
                XAppInterceptor,
                AuthErrorInterceptor,
                ApiToastInterceptor,
                NoCacheInterceptor,
                BlobCacheBusterInterceptor,
            ]),
        ),
        {
            provide: PLAIN_HTTP_CLIENT,
            useFactory: (backend: HttpBackend) => new HttpClient(backend),
            deps: [HttpBackend],
        },
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
                showDialog: false,
            }),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => {
                return () => {
                    // Sentry tracing is initialized when initSentryFromDsn runs after config load
                };
            },
            deps: [Sentry.TraceService],
            multi: true,
        },
        provideTranslateService({
            loader: {
                provide: TranslateLoader,
                useClass: ConfigAwareTranslateLoader,
            },
            defaultLanguage: 'en',
        }),
        providePrimeNG({
            theme: {
                preset: DkPreset,
                options: {
                    darkModeSelector: ':root',
                },
            },
        }),
        provideServiceWorker('ngsw-worker.js', {
            enabled: !isDevMode(),
            registrationStrategy: 'registerWhenStable:30000',
        }),
        provideEchartsCore({ echarts: () => import('echarts') }),

        MessageService,
        ConfirmationService,
        DialogService,
        LookupProxy,
        { provide: API_BASE_URL, useValue: environment.apiBaseUrl },
        UserProxy,
        AccessEnterpriseProxy,
        EnterpriseProxy,
        AccessRoleProxy,
        CropSeasonProxy,
        CropMapUserProxy,
        CropMapPlantationProxy,
        FieldObservationProxy,
        FieldObservationQuestionnaireProxy,
        FieldObservationUserProxy,
        provideAppInitializer(async () => {
            const appConfigService = inject(AppConfigService);
            const langInitService = inject(LanguageInitializerService);
            const appInitService = inject(AppInitializerService);
            const analyticsService = inject(AnalyticsService);
            const authService = inject(AuthService);
            inject(NavigationService);

            try {
                await appConfigService.initializeApp();
                initSentryFromDsn(appConfigService.sentryDsn);
                await langInitService.initializeLanguage();
                await appInitService.initializeApp();
                analyticsService.getStatus();
                authService.restoreSession();
            } catch (error) {
                console.error('Failed to initialize application:', error);
                if (getClient()) {
                    Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
                        tags: { component: 'AppInitializer' },
                        extra: { initializationStep: 'app-bootstrap' },
                        level: 'error',
                    });
                }
            }
        }),
    ],
};
